/**
 * The name a receipt is filed under: the shop's own, as printed, never the
 * registry's legal name.
 *
 * The registry is what the organisation number resolves to, and it is the
 * right thing to store a company by - but its name is the legal entity's:
 * `Bauhaus & Co KB`, `IKEA Svenska Försäljnings AB`, or someone else entirely
 * (`Christian Henke AB` runs Jeansbolaget). The receipt says what the shop is
 * called, so that is the name kept; the company stays linked by its number.
 *
 * What the receipt says is read by a machine, though, and comes back in
 * capitals (`BAUHAUS`) or misspelt (`Baohaus`). So each word of it is checked
 * against the words the shop is already known by - the name learned from
 * earlier receipts, then the registry's - and takes their spelling and case
 * where it is plainly the same word. A word nothing vouches for is kept as
 * read, only brought out of all capitals.
 */

import { foldForMatching, isLegalToken, similarity } from './fuzzy.js';
import type { Company } from './types.js';

/** Registered forms, printed after (or before) a name on some receipts: `BAUHAUS AB`. */
const LEGAL_FORM_WORDS = new Set([
  'ab', 'aktiebolag', 'publ', 'hb', 'kb', 'handelsbolag', 'kommanditbolag',
]);

/** Similarity at which a read word is taken to be a known word, misread. */
const SAME_WORD = 0.75;

/** Words this short are corrected only when they are the same letters: `IKA` is not `ICA`. */
const SHORT_WORD = 3;

/** Whether a word is a legal form, including the dotted and bracketed ways it is printed. */
function isLegalFormWord(word: string): boolean {
  const folded = foldForMatching(word).replace(/ /g, '');
  return LEGAL_FORM_WORDS.has(folded);
}

/** `Ek. för.` and `ekonomisk förening`, which are two words. */
const COOPERATIVE_FORM = /\s*,?\s*\b(ek\.?\s*för\.?|ekonomisk\s+förening)\s*$/i;

/**
 * The name without its registered form: `Bauhaus & Co KB` → `Bauhaus & Co`,
 * `AB Volvo (publ)` → `Volvo`. A name that is nothing but a form is kept.
 */
export function withoutLegalForm(name: string): string {
  const words = name.replace(COOPERATIVE_FORM, '').trim().split(/\s+/).filter(Boolean);
  let start = 0;
  let end = words.length;
  while (start < end && isLegalFormWord(words[start]!)) start += 1;
  while (end > start && isLegalFormWord(words[end - 1]!)) end -= 1;
  const kept = words.slice(start, end).join(' ').replace(/[\s,]+$/, '');
  return kept || name.trim();
}

/**
 * A word out of all capitals: `BAUHAUS` → `Bauhaus`. Short words (`ICA`,
 * `H&M`), words with digits (`OKQ8`) and anything already in mixed case are
 * left as they are, since they are more likely to be spelt that way on purpose.
 */
function titleWord(word: string): string {
  const letters = word.replace(/[^\p{L}]/gu, '');
  if (letters.length <= SHORT_WORD || /\d/.test(word)) return word;
  if (letters !== letters.toLocaleUpperCase('sv')) return word;
  const lower = word.toLocaleLowerCase('sv');
  return lower.charAt(0).toLocaleUpperCase('sv') + lower.slice(1);
}

/** A name out of all capitals, word by word. See {@link titleWord}. */
export function tidyStoreName(name: string): string {
  return name.trim().split(/\s+/).filter(Boolean).map(titleWord).join(' ');
}

/** Whether a name is written in capitals throughout, as some registers and every receipt printer do. */
function allCapitals(name: string): boolean {
  const letters = name.replace(/[^\p{L}]/gu, '');
  return letters.length > 0 && letters === letters.toLocaleUpperCase('sv');
}

/**
 * Words a shop is known by, most trusted first, with legal forms and filler
 * left out. A source in mixed case keeps its capitals - `IKEA Svenska
 * Försäljnings` means `IKEA` - while one in capitals throughout says nothing
 * about case and is tidied.
 */
function vocabulary(sources: readonly (string | null | undefined)[]): string[] {
  const words: string[] = [];
  const seen = new Set<string>();
  for (const source of sources) {
    if (!source) continue;
    const shouting = allCapitals(source);
    for (const raw of source.split(/\s+/)) {
      const word = raw.replace(/^[^\p{L}\p{N}&]+|[^\p{L}\p{N}&]+$/gu, '');
      const folded = foldForMatching(word).replace(/ /g, '');
      if (!folded || isLegalToken(folded) || seen.has(folded)) continue;
      seen.add(folded);
      words.push(shouting ? titleWord(word) : word);
    }
  }
  return words;
}

/** The known word a read word is a reading of, or `null`. */
function knownWord(word: string, known: readonly string[]): string | null {
  const folded = foldForMatching(word).replace(/ /g, '');
  if (!folded) return null;
  let best: { word: string; score: number } | null = null;
  for (const candidate of known) {
    const target = foldForMatching(candidate).replace(/ /g, '');
    if (target === folded) return candidate;
    if (folded.length <= SHORT_WORD || target.length <= SHORT_WORD) continue;
    const score = similarity(folded, target);
    if (score >= SAME_WORD && (!best || score > best.score)) best = { word: candidate, score };
  }
  return best?.word ?? null;
}

/** The printed word with the same letters as a read word, for its case: `WILLYS` → `Willys`. */
function sameLetters(word: string, printed: readonly string[]): string | null {
  const folded = foldForMatching(word).replace(/ /g, '');
  if (!folded) return null;
  return printed.find((candidate) => foldForMatching(candidate).replace(/ /g, '') === folded) ?? null;
}

/** The part of a company the name is corrected against. */
export type StoreNameCompany = Pick<Company, 'name'> & { displayName?: string | null };

/**
 * The name to file a receipt under.
 *
 * - With a name read off the receipt: that name, without a legal form, each
 *   word spelt and cased as the shop is known where it is the same word -
 *   `BAOHAUS` becomes `Bauhaus`, `IKEA KUNGENS KURVA` becomes `IKEA Kungens
 *   Kurva` - and otherwise brought out of all capitals.
 * - With none: the name the shop is known by, else the registry's name
 *   without its legal form.
 *
 * `printed` is other lines of the same receipt, trusted after the company:
 * a header in capitals (`IKEA`, `WiLLY:S`) often has the name in its own
 * spelling a line below (`IKEA Svenska Försäljnings AB`, `Willys Sköndal`).
 * Being read by the same OCR, a printed line lends its case to the same
 * letters only, never its spelling: `JEANSBOLAGET` above a misread
 * `Jeansbolagei` stays `Jeansbolaget`.
 */
export function storeName(
  read: string | null | undefined,
  company: StoreNameCompany | null | undefined,
  printed: readonly string[] = [],
): string | null {
  const learned = company?.displayName?.trim() || null;
  const registered = company ? withoutLegalForm(company.name) : null;

  const scanned = read?.trim() ? withoutLegalForm(read.trim().replace(/\s+/g, ' ')) : '';
  if (!scanned) return learned ?? (registered ? tidyStoreName(registered) : null);

  const known = vocabulary([learned, registered]);
  // Printed lines in capitals throughout say nothing about case, so only mixed-case ones are asked.
  const cased = vocabulary(printed.filter((line) => !allCapitals(line)));
  return scanned
    .split(' ')
    .map((word) => knownWord(word, known) ?? sameLetters(word, cased) ?? titleWord(word))
    .join(' ');
}
